import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MQFailure, MQFailureAttempt } from './types';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import { REPO_CONFIG, formatDate, formatFullDateTime } from './utils';
import { failureReasonContent, failureStageContent } from './failureBadges';
import './QueueEvictions.css';

type SortColumn = 'failure_reason' | 'failed_at';
type SortDir = 'asc' | 'desc';

const chgTicketCell = (chgTicket: string | undefined) =>
  chgTicket ? (
    <a href={`https://teladochealth.atlassian.net/browse/${chgTicket}`} target="_blank" rel="noopener noreferrer">
      {chgTicket}
    </a>
  ) : (
    <span className="text-muted">—</span>
  );

const truncate = (text: string, maxLen: number) =>
  text.length > maxLen ? `${text.slice(0, maxLen).trimEnd()}…` : text;

type HistoryItem =
  | { kind: 'date'; key: string; label: string }
  | { kind: 'build'; key: string; attempt: MQFailureAttempt };

// Splits a PR's older failed builds into date-grouped rows for the expanded view —
// one label row per calendar day, followed by that day's build rows.
const buildHistoryItems = (attempts: MQFailureAttempt[]): HistoryItem[] => {
  const items: HistoryItem[] = [];
  let lastDateKey = '';
  for (const attempt of attempts) {
    const d = new Date(attempt.failedAt);
    const dateKey = d.toDateString();
    if (dateKey !== lastDateKey) {
      items.push({
        kind: 'date',
        key: `date-${dateKey}`,
        label: d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
      });
      lastDateKey = dateKey;
    }
    items.push({ kind: 'build', key: `${attempt.buildNumber}-${attempt.failedAt}`, attempt });
  }
  return items;
};

export const QueueEvictions: React.FC = () => {
  const [allFailures, setAllFailures] = useState<MQFailure[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [prNumberInput, setPrNumberInput] = useState('');
  const [chgTicketInput, setChgTicketInput] = useState('');

  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const getToken = useAccessToken();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchApi<MQFailure[]>(
        `/api/mergequeue/recent-failures?organization=${REPO_CONFIG.organization}&repo=${REPO_CONFIG.repo}`,
        getToken
      );
      setAllFailures(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load MQ failures');
      setAllFailures([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Every build (latest + previousFailures) is already fully resolved by the API, so
  // expanding a row is a pure UI toggle — no on-demand fetch needed.
  const toggleRow = (f: MQFailure) => {
    if (f.previousFailures.length === 0) return;
    setExpandedRows(prev => ({ ...prev, [f.prNumber]: !prev[f.prNumber] }));
  };

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDir('asc');
    }
  };

  const sortIcon = (col: SortColumn) => {
    if (sortColumn !== col) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon sort-icon--active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const sortedFailures = useMemo(() => {
    const filtered = allFailures.filter(f => {
      if (prNumberInput.trim() && !(f.prNumber?.toString() ?? '').includes(prNumberInput.trim())) return false;
      if (chgTicketInput.trim() && !(f.chgTicket || '').toLowerCase().includes(chgTicketInput.toLowerCase().trim())) return false;
      return true;
    });

    if (!sortColumn) return filtered;

    return [...filtered].sort((a, b) => {
      if (sortColumn === 'failure_reason') {
        const aName = a.pipelineStages?.[0]?.name ?? '';
        const bName = b.pipelineStages?.[0]?.name ?? '';
        return sortDir === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
      }
      // failed_at
      const aTime = new Date(a.failedAt).getTime();
      const bTime = new Date(b.failedAt).getTime();
      return sortDir === 'asc' ? aTime - bTime : bTime - aTime;
    });
  }, [allFailures, prNumberInput, chgTicketInput, sortColumn, sortDir]);

  const resultLabel = loading
    ? 'Loading...'
    : `${sortedFailures.length} MQ Failure${sortedFailures.length !== 1 ? 's' : ''}`;

  return (
    <div className="failed-prs-container">

      {/* Search form */}
      <div className="failed-prs-search">
        <div className="search-form-row">
          <div className="search-field">
            <label className="search-label">PR Number</label>
            <input
              type="text"
              className="search-input"
              placeholder="e.g. 106372"
              value={prNumberInput}
              onChange={e => setPrNumberInput(e.target.value)}
            />
          </div>
          <div className="search-field">
            <label className="search-label">CHG Ticket</label>
            <input
              type="text"
              className="search-input"
              placeholder="e.g. CHG-19976"
              value={chgTicketInput}
              onChange={e => setChgTicketInput(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Results summary */}
      <div className="failed-prs-results-header">
        <span className="failed-prs-count">
          <span>🔴</span> {resultLabel}
        </span>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">❌ {error}</p>
        </div>
      )}

      {/* Table */}
      <div className="queue-table-container">
        <table className="queue-table">
          <colgroup>
            <col style={{ width: '2.5rem' }} />
            <col />
            <col style={{ width: '10%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '28%' }} />
            <col style={{ width: '11%' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="col-expand" />
              <th className="col-pr">Pull Request</th>
              <th className="col-chg-ticket">CHG Ticket</th>
              <th className="col-ci-stage sortable" onClick={() => handleSort('failure_reason')}>
                Failure Stage {sortIcon('failure_reason')}
              </th>
              <th className="col-stage">Failure Reason</th>
              <th className="col-failed-at sortable" onClick={() => handleSort('failed_at')}>
                Failed On {sortIcon('failed_at')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="empty-queue-row">
                  <div className="empty-queue-message">Loading MQ failures…</div>
                </td>
              </tr>
            ) : sortedFailures.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-queue-row">
                  <div className="empty-queue-message">
                    {allFailures.length === 0
                      ? '🎉 No MQ failures found!'
                      : 'No results match your filter'}
                  </div>
                </td>
              </tr>
            ) : (
              sortedFailures.map(f => {
                const hasHistory = f.previousFailures.length > 0;
                const expanded = hasHistory && !!expandedRows[f.prNumber];
                return (
                  <React.Fragment key={f.prNumber}>
                    <tr className={expanded ? 'mq-row--expanded' : undefined}>
                      <td
                        className={`col-expand${hasHistory ? ' col-expand--clickable' : ''}`}
                        onClick={hasHistory ? () => toggleRow(f) : undefined}
                        title={hasHistory ? (expanded ? 'Collapse' : 'Expand') : undefined}
                      >
                        {hasHistory ? (expanded ? '▾' : '▸') : ''}
                      </td>
                      <td className="col-pr">
                        <a href={f.prUrl} target="_blank" rel="noopener noreferrer" className="pr-link">
                          {f.prTitle} <span className="pr-number-muted">(#{f.prNumber})</span>
                        </a>
                        {hasHistory && (
                          <button type="button" className="mq-more-link" onClick={() => toggleRow(f)}>
                            +{f.previousFailures.length} more...
                          </button>
                        )}
                      </td>
                      <td className="col-chg-ticket">
                        {chgTicketCell(f.chgTicket)}
                      </td>
                      <td className="col-ci-stage">
                        {failureReasonContent(f.pipelineStages, f.parentBuildUrl)}
                      </td>
                      <td className="col-stage">
                        {failureStageContent(f.failureStages)}
                      </td>
                      <td className="col-failed-at">
                        <span
                          className="timestamp-rel"
                          data-full={formatFullDateTime(f.failedAt)}
                          onMouseMove={e => {
                            e.currentTarget.style.setProperty('--tip-x', `${e.clientX + 12}px`);
                            e.currentTarget.style.setProperty('--tip-y', `${e.clientY + 16}px`);
                          }}
                        >
                          {formatDate(f.failedAt)}
                        </span>
                      </td>
                    </tr>

                    {expanded && (
                      <>
                        <tr className="mq-history-section-row mq-history-block-start">
                          <td className="col-expand" />
                          <td className="mq-history-section-label" colSpan={5}>Previous Failures</td>
                        </tr>

                        {buildHistoryItems(f.previousFailures).map((item, idx, items) => {
                          const edgeClass = idx === items.length - 1 ? ' mq-history-block-end' : '';

                          if (item.kind === 'date') {
                            return (
                              <tr className={`mq-history-date-row${edgeClass}`} key={item.key}>
                                <td className="col-expand" />
                                <td className="mq-history-date-label" colSpan={5}>{item.label}</td>
                              </tr>
                            );
                          }

                          const { attempt } = item;
                          return (
                            <tr className={`mq-history-row${edgeClass}`} key={item.key}>
                              <td className="col-expand" />
                              <td className="col-pr">
                                <span className="mq-history-pr-snippet" title={f.prTitle}>
                                  {truncate(f.prTitle, 40)}
                                </span>
                                <span className="mq-history-build-num">Build #{attempt.buildNumber}</span>
                              </td>
                              <td className="col-chg-ticket">
                                {chgTicketCell(f.chgTicket)}
                              </td>
                              <td className="col-ci-stage">
                                {failureReasonContent(attempt.pipelineStages, attempt.parentBuildUrl)}
                              </td>
                              <td className="col-stage">
                                {failureStageContent(attempt.failureStages)}
                              </td>
                              <td className="col-failed-at">
                                <span
                                  className="timestamp-rel"
                                  data-full={formatFullDateTime(attempt.failedAt)}
                                  onMouseMove={e => {
                                    e.currentTarget.style.setProperty('--tip-x', `${e.clientX + 12}px`);
                                    e.currentTarget.style.setProperty('--tip-y', `${e.clientY + 16}px`);
                                  }}
                                >
                                  {formatDate(attempt.failedAt)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
